import React, { useRef } from 'react';

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.55 }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
    <polyline points="13 2 13 9 20 9"/>
  </svg>
);

// value can be:
//   undefined / null  → no file
//   string            → existing file URL from API (edit mode)
//   File              → newly selected file object
const FileInputWidget = ({ label, required, value, onChange }) => {
  const inputRef = useRef(null);

  const isExistingUrl = typeof value === 'string' && value.length > 0;
  const isNewFile     = value instanceof File;
  const hasFile       = isExistingUrl || isNewFile;

  const handleZoneClick = () => inputRef.current?.click();

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  };

  // Display name for existing URL: grab last segment, strip query params
  const existingName = isExistingUrl
    ? (value.split('/').pop() || '').split('?')[0] || 'Current file'
    : null;

  return (
    <div className="form-file-wrap">
      <label className="form-label">
        {label}
        {required && <span className="req">*</span>}
      </label>

      {/* Hidden native input */}
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        required={required && !hasFile}
        onChange={(e) => onChange(e.target.files[0] ?? null)}
      />

      {/* Clickable upload zone */}
      <div
        className={`form-file-zone${isNewFile ? ' form-file-zone--selected' : ''}`}
        onClick={handleZoneClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleZoneClick()}
      >
        <UploadIcon />
        <span className="form-file-zone-text">
          {isNewFile ? value.name : 'Click to choose a file…'}
        </span>
        {isNewFile ? (
          <button
            type="button"
            className="form-file-clear"
            onClick={handleClear}
            title="Clear selection"
            aria-label="Clear selected file"
          >
            ✕
          </button>
        ) : (
          <span className="form-file-browse-btn">Browse</span>
        )}
      </div>

      {/* Existing file row — shown when editing and no replacement chosen yet */}
      {isExistingUrl && !isNewFile && (
        <div className="form-file-existing">
          <FileIcon />
          <span className="form-file-existing-label">Current file:</span>
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="form-file-existing-link"
            onClick={(e) => e.stopPropagation()}
          >
            {existingName}
          </a>
          <span className="form-file-existing-hint">— click above to replace</span>
          <button
            type="button"
            className="form-file-remove"
            onClick={handleClear}
            title="Remove file"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

export default FileInputWidget;
